/**
 * DS-internal hook : le conteneur est-il au moins aussi large que `threshold` ?
 *
 * Une disposition qui dépend de la place (une ligne ou deux niveaux, le bouton à côté ou dessous)
 * se tranche sur la largeur DU CONTENEUR, lue par `onLayout`, jamais sur celle de la fenêtre : la
 * même ligne vit dans une colonne, une page et une feuille, qui n'ont pas la même largeur au même
 * moment.
 *
 * Le crochet garde le BOOLÉEN, pas la largeur : React ignore une mise à jour identique, donc la vue
 * ne se re-rend qu'au passage du seuil — ni à chaque pixel d'un redimensionnement, ni après le
 * premier layout d'une vue qui reste étroite. Sans estimation, il démarre à `false` : on démarre étroit, parce
 * que ce qui se replie se verrait.
 *
 * **Une estimation pose la première image** (Wajih, 05/11 : l'accueil du masteur « se redimensionne au
 * chargement »). Au web, `onLayout` arrive APRÈS la première image : un bloc parti étroit s'y montrait étroit, puis
 * s'élargissait — l'accueil en une colonne, « GO ! » sur toute la largeur, puis deux colonnes. Un conteneur qui sait
 * la largeur qu'il donnera l'annonce à ce qu'il contient (`<EstimatedWidth width>`) ; un crochet dessous part de
 * cette largeur, et la mesure garde le dernier mot. Sans annonce, rien ne change.
 *
 * `threshold` est une constante de l'appelant : un seuil qui bougerait sans que la largeur bouge ne
 * serait relu qu'au layout suivant.
 */
import React, { createContext, useCallback, useContext, useRef, useState } from "react"
import { type LayoutChangeEvent } from "react-native"

// La valeur du contexte est une boîte stable : l'annonce ne se lit qu'au montage, et un changement de largeur (la
// fenêtre, une colonne repliée) ne re-rend pas ce qui l'a lue — la mesure a pris le relais.
const EstimatedWidthContext = createContext<{ current: number | null } | null>(null)

/** La largeur que ce conteneur donnera à ce qu'il contient, avant toute mesure (`null` : inconnue). */
export function EstimatedWidth(props: { width: number | null; children: React.ReactNode }) {
    const box = useRef<number | null>(props.width)
    box.current = props.width
    return React.createElement(EstimatedWidthContext.Provider, { value: box }, props.children)
}

/** La largeur annoncée par le conteneur le plus proche, `null` sans annonce. */
export function useEstimatedWidth(): number | null {
    return useContext(EstimatedWidthContext)?.current ?? null
}

export type WidthAtLeast = {
    /** À poser sur la vue mesurée. */
    onLayout: (event: LayoutChangeEvent) => void
    /** Vrai quand la dernière largeur mesurée atteint le seuil ; avant toute mesure, l'estimation (faux sans elle). */
    atLeast: boolean
}

export function useWidthAtLeast(threshold: number): WidthAtLeast {
    const estimate = useEstimatedWidth()
    const [atLeast, setAtLeast] = useState(() => estimate != null && estimate >= threshold)
    const onLayout = useCallback(
        (event: LayoutChangeEvent) => setAtLeast(event.nativeEvent.layout.width >= threshold),
        [threshold]
    )
    return { onLayout, atLeast }
}
